import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { Faq, TouchPoint, ModuleType } from '../types/models';

export interface CreateFaqData {
  question: string;
  answer: string;
  category?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface CreateTouchPointData {
  name: string;
  description?: string;
  type: 'phone' | 'email' | 'address' | 'social' | 'other';
  value: string;
  is_active?: boolean;
  sort_order?: number;
}

export interface CreateModuleTypeData {
  name: string;
  description?: string;
  code: string;
}

class SupportService {
  private faqBaseUrl = '/admin/county/faqs';
  private touchPointBaseUrl = '/admin/county/touch-points';
  private moduleTypeBaseUrl = '/admin/county/module-types';

  // FAQ endpoints
  async getFaqs(page?: number): Promise<ApiResponse<PaginatedResponse<Faq>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<Faq>>(`${this.faqBaseUrl}${params}`);
  }

  async getFaq(id: number): Promise<ApiResponse<Faq>> {
    return apiClient.get<Faq>(`${this.faqBaseUrl}/${id}`);
  }

  async createFaq(data: CreateFaqData): Promise<ApiResponse<Faq>> {
    return apiClient.post<Faq>(this.faqBaseUrl, data);
  }

  async updateFaq(id: number, data: Partial<CreateFaqData>): Promise<ApiResponse<Faq>> {
    return apiClient.put<Faq>(`${this.faqBaseUrl}/${id}`, data);
  }

  async deleteFaq(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.faqBaseUrl}/${id}`);
  }

  async toggleFaqStatus(id: number): Promise<ApiResponse<Faq>> {
    return apiClient.post<Faq>(`${this.faqBaseUrl}/${id}/toggle-status`);
  }

  async reorderFaqs(items: Array<{ id: number; sort_order: number }>): Promise<ApiResponse> {
    return apiClient.post(`${this.faqBaseUrl}/reorder`, { items });
  }

  // TouchPoint endpoints
  async getTouchPoints(page?: number): Promise<ApiResponse<PaginatedResponse<TouchPoint>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<TouchPoint>>(`${this.touchPointBaseUrl}${params}`);
  }

  async getTouchPoint(id: number): Promise<ApiResponse<TouchPoint>> {
    return apiClient.get<TouchPoint>(`${this.touchPointBaseUrl}/${id}`);
  }

  async createTouchPoint(data: CreateTouchPointData): Promise<ApiResponse<TouchPoint>> {
    return apiClient.post<TouchPoint>(this.touchPointBaseUrl, data);
  }

  async updateTouchPoint(id: number, data: Partial<CreateTouchPointData>): Promise<ApiResponse<TouchPoint>> {
    return apiClient.put<TouchPoint>(`${this.touchPointBaseUrl}/${id}`, data);
  }

  async deleteTouchPoint(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.touchPointBaseUrl}/${id}`);
  }

  async toggleTouchPointStatus(id: number): Promise<ApiResponse<TouchPoint>> {
    return apiClient.post<TouchPoint>(`${this.touchPointBaseUrl}/${id}/toggle-status`);
  }

  async reorderTouchPoints(items: Array<{ id: number; sort_order: number }>): Promise<ApiResponse> {
    return apiClient.post(`${this.touchPointBaseUrl}/reorder`, { items });
  }

  // ModuleType endpoints
  async getModuleTypes(page?: number): Promise<ApiResponse<PaginatedResponse<ModuleType>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<ModuleType>>(`${this.moduleTypeBaseUrl}${params}`);
  }

  async getModuleType(id: number): Promise<ApiResponse<ModuleType>> {
    return apiClient.get<ModuleType>(`${this.moduleTypeBaseUrl}/${id}`);
  }

  async createModuleType(data: CreateModuleTypeData): Promise<ApiResponse<ModuleType>> {
    return apiClient.post<ModuleType>(this.moduleTypeBaseUrl, data);
  }

  async updateModuleType(id: number, data: Partial<CreateModuleTypeData>): Promise<ApiResponse<ModuleType>> {
    return apiClient.put<ModuleType>(`${this.moduleTypeBaseUrl}/${id}`, data);
  }

  async deleteModuleType(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.moduleTypeBaseUrl}/${id}`);
  }
}

export const supportService = new SupportService();
export default supportService;
